import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Grid, Paper, MenuItem, Select, FormControl } from "@mui/material";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../../config/firebase";
import { COLLECTION } from "../../../../data/constant";

/* ---------------- DATE HELPERS ---------------- */
const formatDate = (date) => date.toISOString().split("T")[0];
const formatMonth = (date) => `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}`;
const getTodayRange = () => {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  return { start, end };
};
const getCurrentMonthRange = () => {
  const start = new Date();
  start.setDate(1);
  start.setHours(0, 0, 0, 0);
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  return { start, end };
};

/* ---------------- CARD STYLE ---------------- */
const cardStyle = {
  p: 2,
  borderRadius: 3,
  boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  width: 160,
  fontFamily: "'Montserrat', sans-serif",
  "&:hover": {
    boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
    transform: "translateY(-2px)",
  },
};

/* ---------------- RANGE OPTIONS ---------------- */
const RANGE_OPTIONS = [7, 15, 30, 90, 180];

const SalesTab = () => {
  const [transactions, setTransactions] = useState([]);
  const [userCount, setUserCount] = useState(0);
  const [todaySales, setTodaySales] = useState(0);
  const [currentMonthSalesAmount, setCurrentMonthSalesAmount] = useState(0);
  const [currentMonthSalesCount, setCurrentMonthSalesCount] = useState(0);

  // Filters
  const [revenueRange, setRevenueRange] = useState(30);
  const [ordersRange, setOrdersRange] = useState(30);
  const [profitRange, setProfitRange] = useState(180);

  /* ---------------- FETCH DATA ---------------- */
  useEffect(() => {
    const fetchData = async () => {
      const txSnap = await getDocs(collection(db, COLLECTION.TRANSACTIONS));
      setTransactions(txSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

      const usersSnap = await getDocs(collection(db, COLLECTION.USER));
      setUserCount(usersSnap.docs.length);
    };
    fetchData();
  }, []);

  /* ---------------- TODAY SALES ---------------- */
  useEffect(() => {
    const { start, end } = getTodayRange();
    const sum = transactions.reduce((acc, tx) => {
      if (!tx.transactionTime || !tx.transactionAmount) return acc;
      const d = tx.transactionTime.toDate();
      return d >= start && d <= end ? acc + tx.transactionAmount : acc;
    }, 0);
    setTodaySales(sum);
  }, [transactions]);

  /* ---------------- CURRENT MONTH SALES ---------------- */
  useEffect(() => {
    const { start, end } = getCurrentMonthRange();
    let amount = 0;
    let count = 0;
    transactions.forEach((tx) => {
      if (!tx.transactionTime || !tx.transactionAmount) return;
      const d = tx.transactionTime.toDate();
      if (d >= start && d <= end) {
        amount += tx.transactionAmount;
        count += 1;
      }
    });
    setCurrentMonthSalesAmount(amount);
    setCurrentMonthSalesCount(count);
  }, [transactions]);

  /* ---------------- KPI CALCULATIONS ---------------- */
  const totalSales = useMemo(() => transactions.reduce((sum, tx) => sum + (tx.transactionAmount || 0), 0), [transactions]);
  const totalOrders = useMemo(() => transactions.filter((tx) => tx.transactionAmount).length, [transactions]);
  const averageOrderValue = totalOrders > 0 ? totalSales / totalOrders : 0;
  const profit = totalSales * 0.1;
  const liability = userCount * 50;

  /* ---------------- GRAPH DATA GENERATOR ---------------- */
  const generateGraphData = (rangeDays) => {
    const today = new Date();
    const startDate = new Date();
    startDate.setDate(today.getDate() - rangeDays + 1);
    const useMonthly = rangeDays > 30;

    const map = {};
    transactions.forEach((tx) => {
      if (!tx.transactionTime || !tx.transactionAmount) return;
      const d = tx.transactionTime.toDate();
      if (d < startDate || d > today) return;
      const key = useMonthly ? formatMonth(d) : formatDate(d);
      if (!map[key]) map[key] = { x: key, revenue: 0, orders: 0, profit: 0, liability: 0 };
      map[key].revenue += tx.transactionAmount;
      map[key].orders += 1;
      map[key].profit += tx.transactionAmount * 0.1;
    });

    // Liability for all
    Object.values(map).forEach((v) => (v.liability = userCount * 50));

    // Fill missing dates/months
    const result = [];
    if (!useMonthly) {
      for (let d = new Date(startDate); d <= today; d.setDate(d.getDate() + 1)) {
        const key = formatDate(new Date(d));
        result.push(map[key] || { x: key, revenue: 0, orders: 0, profit: 0, liability: userCount * 50 });
      }
    } else {
      const startMonth = new Date(today.getFullYear(), today.getMonth() - Math.ceil(rangeDays / 30) + 1, 1);
      for (let i = 0; i < Math.ceil(rangeDays / 30); i++) {
        const d = new Date(startMonth.getFullYear(), startMonth.getMonth() + i, 1);
        const key = formatMonth(d);
        result.push(map[key] || { x: key, revenue: 0, orders: 0, profit: 0, liability: userCount * 50 });
      }
    }

    return result;
  };

  const revenueData = useMemo(() => generateGraphData(revenueRange), [transactions, revenueRange]);
  const ordersData = useMemo(() => generateGraphData(ordersRange), [transactions, ordersRange]);
  const profitData = useMemo(() => generateGraphData(profitRange), [transactions, profitRange]);

  return (
    <Box sx={{ mt: 2, fontFamily: "'Montserrat', sans-serif" }}>
      {/* ---------------- KPI CARDS ---------------- */}
      <Grid container spacing={2} justifyContent="space-between" >
        {[
          ["Total Sales", `₹${totalSales}`],
          ["Today's Sales", `₹${todaySales}`],
          ["Monthly Sales Amount", `₹${currentMonthSalesAmount}`],
          ["Monthly Sales Count", `${currentMonthSalesCount}`],
          ["Average Order Value", `₹${averageOrderValue.toFixed(2)}`],
          ["Total Profit (10%)", `₹${profit.toFixed(2)}`],
          ["Total Liability (Wallet)", `₹${liability}`],
        ].map(([label, value]) => (
          <Grid item xs="auto" key={label}>
            <Paper sx={{ ...cardStyle, minWidth: 140, p: 1.5 }}>
              <Typography variant="subtitle2" color="textSecondary" sx={{ fontFamily: "Montserrat" }}>{label}</Typography>
              <Typography variant="h6" sx={{ mt: 0.5 }}>{value}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* ---------------- GRAPHS ---------------- */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* Revenue Trend */}
        <Grid item xs={12} md="auto" sx={{ flex: 1, minWidth: 300 }}>
          <Paper sx={{ p: 2, borderRadius: 3, height: 350 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography variant="subtitle2" color="textSecondary" sx={{ fontFamily: "Montserrat" }}>Revenue Trend</Typography>
              <FormControl size="small" sx={{ minWidth: 80 , fontFamily: "Montserrat"}}>
                <Select value={revenueRange} onChange={(e) => setRevenueRange(Number(e.target.value))}>
                  {RANGE_OPTIONS.map((v) => <MenuItem key={v} value={v}>{v}d</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
            <ResponsiveContainer width="100%" height="90%">
              <BarChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="x" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="revenue" fill="#1976d2" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Orders Trend */}
        <Grid item xs={12} md="auto" sx={{ flex: 1, minWidth: 300 }}>
          <Paper sx={{ p: 2, borderRadius: 3, height: 350 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography variant="subtitle2" color="textSecondary" sx={{ fontFamily: "Montserrat" }}>Sales Trend</Typography>
              <FormControl size="small" sx={{ minWidth: 80 , fontFamily: "Montserrat"}}>
                <Select value={ordersRange} onChange={(e) => setOrdersRange(Number(e.target.value))}>
                  {RANGE_OPTIONS.map((v) => <MenuItem key={v} value={v}>{v}d</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
            <ResponsiveContainer width="100%" height="90%">
              <LineChart data={ordersData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="x" />
                <YAxis />
                <Tooltip />
                <Line dataKey="orders" stroke="#2e7d32" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Profit vs Liability */}
        <Grid item xs={12} md="auto" sx={{ flex: 1, minWidth: 350 }}>
          <Paper sx={{ p: 2, borderRadius: 3, height: 350 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography variant="subtitle2" color="textSecondary" sx={{ fontFamily: "Montserrat" }}>Profit vs Liability</Typography>
              <FormControl size="small" sx={{ minWidth: 80 ,fontFamily: "Montserrat"}}>
                <Select value={profitRange} onChange={(e) => setProfitRange(Number(e.target.value))}>
                  {RANGE_OPTIONS.map((v) => <MenuItem key={v} value={v}>{v}d</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
            <ResponsiveContainer width="100%" height="90%">
              <BarChart data={profitData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="x" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="profit" fill="#1976d2" name="Profit" />
                <Bar dataKey="liability" fill="#2e7d32" name="Liability" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SalesTab;
